// src/components/Filter/Filter.jsx
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { QueryArgsContext } from '../../views/Menu.jsx';
import { useCart } from "../../contexts/CartContext";
import { useFilter } from '../../contexts/FilterContext';
import { useConfig } from '../../contexts/ConfigContext';

// Import hooks
import { useFilterOptions } from '../../hooks/filter/useFilterOptions';
import { useCategorySubcategories } from '../../hooks/filter/useCategorySubcategories';

// Import components
import Pills from './Pills.jsx';
import FilterModules from './FilterModules.jsx';
import SearchBar from './SearchBar.jsx';
import OrderTypeManager from '../Cart/OrderTypeManager.jsx';

const Filter = ({
    categoriesToShow = [],
    hideCategoryFilter,
    hideSubcategoryFilter,
    hideTagsFilter,
    hideTerpenesFilter,
    showDeliveryPickup,
    forcedOrderType,
}) => {
    const { styleOptions } = useConfig();
    
    // Get filter context values
    const {
        selectedFilters,
        clearAllFilters,
        mobileFilterOpen,
        closeMobileFilter,
        filterTerms,
        retailerId,
        specialsExist,
        thcMin,
    } = useFilter();
    
    // Other contexts
    const queryArgs = useContext(QueryArgsContext);
    
    // Refs
    const filterWrapperRef = useRef(null);

    // Data fetching
    const { cartData, isFetched: isFetchedCartData } = useCart();

    // Memoized values for subcategory fetching
    const memoizedRetailerId = useMemo(() => retailerId, [retailerId]);
    const memoizedPricingType = useMemo(() => cartData?.pricingType || '', [cartData?.pricingType]);
    const memoizedSelectedCategory = useMemo(() => selectedFilters?.category || '', [selectedFilters?.category]);

    // Fetch subcategories for selected category
    const shouldFetchSubcategories = !!(memoizedSelectedCategory && memoizedRetailerId && memoizedPricingType);
    const categorySubcategoriesResult = useCategorySubcategories(
        memoizedRetailerId,
        memoizedSelectedCategory,
        memoizedPricingType,
        shouldFetchSubcategories
    );

    const { availableSubcategories, hasCategory } = shouldFetchSubcategories
        ? categorySubcategoriesResult
        : { availableSubcategories: new Set(), hasCategory: false };

    // Local state for filter options processing
    const [topLevelFilterKey, setTopLevelFilterKey] = useState('');
    const [hasSubFiltersSelected, setHasSubFiltersSelected] = useState(false);
    const [mobileDisplayAllFilters, setMobileDisplayAllFilters] = useState(false);
    const [showOrderType, setShowOrderType] = useState(showDeliveryPickup);

    // Calculate filter states
    useEffect(() => {
        const hasAnySubFilterSelected =
            (selectedFilters.weights?.length > 0) ||
            (selectedFilters.effects?.length > 0) ||
            (selectedFilters.tags?.length > 0) ||
            !!selectedFilters.subcategory ||
            !!selectedFilters.strain;

        const newTopLevelFilterKey = `${selectedFilters.category || ''}-${selectedFilters.brandIds?.join(',') || ''}-${selectedFilters.specials || ''}-${selectedFilters.staff_picks || ''}`;

        setHasSubFiltersSelected(hasAnySubFilterSelected);
        setTopLevelFilterKey(newTopLevelFilterKey);
    }, [selectedFilters]);

    // Get processed filter options
    const { processedFilterTerms } = useFilterOptions(
        filterTerms,
        selectedFilters,
        categoriesToShow,
        availableSubcategories,
        hasCategory
    );


    // Event handlers
    const handleCloseFilters = () => {
        closeMobileFilter();
        setMobileDisplayAllFilters(false);
    };

    const handleOverlayClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearAllFilters();
        closeMobileFilter();
    };

    return (
        <>
            {showOrderType && (
                <div className='filter-sidebar-ordertype filter-sidebar-ordertype-mobile'>
                    <OrderTypeManager forcedOrderType={forcedOrderType} />
                </div>
            )}

            {mobileFilterOpen && (
                <div
                    onClick={handleOverlayClick}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleOverlayClick(e);
                        }
                    }}
                    role="button"
                    tabIndex="0"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        cursor: 'pointer',
                    }}
                />
            )}

            <div
                id="filter-sidebar-content"
                className={`headless-filter-wrapper ${mobileFilterOpen ? 'filter-toggled' : ''}`}
                ref={filterWrapperRef}
                role="complementary"
                aria-label="Product filters"
            >
                {showOrderType && (
                    <div className='filter-sidebar-ordertype filter-sidebar-ordertype-desktop'>
                        <OrderTypeManager forcedOrderType={forcedOrderType} />
                    </div>
                )}

                <button
                    className='mobile-filter-close-x'
                    onClick={handleCloseFilters}
                    aria-label="Close filters"
                >
                    <div className='mobile-filter-close-x-icon' aria-hidden='true'>
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'>
                            <path d='M376.6 84.5c11.3-13.6 9.5-33.8-4.1-45.1s-33.8-9.5-45.1 4.1L192 206 56.6 43.5C45.3 29.9 25.1 28.1 11.5 39.4S-3.9 70.9 7.4 84.5L150.3 256 7.4 427.5c-11.3 13.6-9.5 33.8 4.1 45.1s33.8 9.5 45.1-4.1L192 306 327.4 468.5c11.3 13.6 31.5 15.4 45.1 4.1s15.4-31.5 4.1-45.1L233.7 256 376.6 84.5z' />
                        </svg>
                    </div>
                </button>

                <div className="headless-filter-wrapper-inner">
                    <div className="headless-desktop-filter-search">
                        <SearchBar />
                    </div>

                    <Pills />

                    {Object.keys(processedFilterTerms).length > 1 && (
                        <FilterModules
                            styleOptions={styleOptions}
                            categoriesToShow={categoriesToShow}
                            specialsExist={specialsExist}
                            menuFilterTerms={processedFilterTerms}
                            hideFilters={{
                                cat: hideCategoryFilter,
                                subcat: hideSubcategoryFilter,
                                tags: hideTagsFilter,
                                terpenes: hideTerpenesFilter,
                            }}
                            mobileDisplay={{
                                toggleMobileDisplayAllFilters: mobileDisplayAllFilters,
                                setToggleMobileDisplayAllFilters: setMobileDisplayAllFilters,
                            }}
                            thcMin={thcMin}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(Filter);
